import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import styles from "./ImageGenerator.module.css";
import { fal } from "@fal-ai/client";

// Configure F A L client
fal.config({
  credentials: process.env.REACT_APP_FAL_KEY,
});

// Format cost as per user rules
function formatCost(value) {
  if (value >= 100) {
    const roundedUp = Math.ceil(value) / 100;
    return `$${roundedUp.toFixed(2)}`;
  } else {
    let c = Number(value).toFixed(3); // up to 3 decimals
    c = c.replace(/\.?0+$/, '');
    return `${c}¢`;
  }
}

// Custom styles for react-select
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#111",
    color: "#f0f0f0",
    borderColor: "#444",
    minHeight: "42px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#111",
    color: "#f0f0f0",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#222" : "#111",
    color: "#f0f0f0",
    cursor: "pointer",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#00b4d8",
    color: "#111",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#111",
    fontWeight: "bold",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#111",
    ":hover": {
      backgroundColor: "#009ec1",
      color: "#fff",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#f0f0f0",
  }),
  input: (provided) => ({
    ...provided,
    color: "#f0f0f0"
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#777"
  }),
  
};

const ImageGenerator = () => {
  const advMode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("adv");

  const [options, setOptions] = useState({
    models: [],
    sizes: {},
    messages: {},
  });
  const [optsLoaded, setOptsLoaded] = useState(false);
  const [initDone, setInitDone] = useState(false);
  const [config, setConfig] = useState({
    prompt: "",
    model: [],
    imageSize: "",
    batch: 1,
    seed: "",
    numSteps: 0,
    guidanceScale: 0,
    costAlert: 0,
  });
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalUrl, setModalUrl] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCostAlertModal, setShowCostAlertModal] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetch("/config.json?v=" + Date.now())
      .then((res) => res.json())
      .then((data) => {
        const { models, sizes, messages = {} } = data;

        let stored = {};
        try {
          stored = JSON.parse(localStorage.getItem("dreamBatchPreset") || "{}");
        } catch {
          stored = {};
        }
        // Defensive: only keep models that exist in config.json
        let modelList = [];
        if (stored.model) {
          if (Array.isArray(stored.model)) {
            modelList = stored.model.filter(m =>
              models.some(mod => mod.apifragment === m)
            );
          } else if (
            typeof stored.model === "string" &&
            models.some(mod => mod.apifragment === stored.model)
          ) {
            modelList = [stored.model];
          }
        }
        if (modelList.length === 0 && models.length > 0) {
          modelList = [models[0].apifragment];
        }

        const firstSizeKey = Object.keys(sizes)[0];
        const firstModel = models[0];

        setOptions(data);
        setConfig({
          prompt: stored.prompt || "",
          model: modelList,
          imageSize: stored.imageSize || firstSizeKey,
          batch: stored.batch || 1,
          seed: stored.seed || "",
          numSteps: stored.numSteps || firstModel.steps,
          guidanceScale: stored.guidanceScale || firstModel.cfg,
          costAlert: messages.cost ?? 0,
        });

        setOptsLoaded(true);
        setInitDone(true);
      })
      .catch((err) => {
        console.error("Failed to load config.json", err);
        setOptsLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (!initDone) return;
    const { costAlert, ...toStore } = config;
    localStorage.setItem("dreamBatchPreset", JSON.stringify(toStore));
  }, [config, initDone]);

  useEffect(() => {
    if (/^\d+$/.test(config.seed)) {
      setConfig((c) => ({ ...c, batch: 1 }));
    }
  }, [config.seed]);

  useEffect(() => {
    if (!options.models.length || !config.model.length || config.model.length > 1) return;
    const sel = options.models.find(
      (m) => m.apifragment === config.model[0]
    );
    if (sel) {
      setConfig((c) => ({
        ...c,
        numSteps: sel.steps,
        guidanceScale: sel.cfg,
      }));
    }
  }, [config.model, options.models]);

  // react-select multi
  const handleModelChange = (selectedOptions) => {
    const selected = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setConfig((c) => ({
      ...c,
      model: selected,
      seed: "",
    }));
    if (selected.length > 1 && config.batch !== 1) {
      handleChange("batch", 1);
    }
  };

  const selectedModels = options.models.filter(m =>
    config.model.includes(m.apifragment)
  );
  const isMultiModel = config.model.length > 1;
  const actualBatch = /^\d+$/.test(config.seed) ? 1 : config.batch;
  const modelsCost = selectedModels.reduce((sum, m) => sum + (m.cost || 0), 0);
  const totalCost = isMultiModel ? modelsCost : modelsCost * actualBatch;
  const costDisplay = formatCost(totalCost);

  const handleChange = (field, value) =>
    setConfig((c) => ({ ...c, [field]: value }));

  const handleThumbnailClick = (url) => setModalUrl(url);
  const closeModal = () => setModalUrl(null);

  const resetToDefaults = () => {
    const { models, sizes, messages = {} } = options;
    const firstModel = models[0];
    const firstSizeKey = Object.keys(sizes)[0];
    setConfig({
      prompt: "",
      model: [firstModel.apifragment],
      imageSize: firstSizeKey,
      batch: 1,
      seed: "",
      numSteps: firstModel.steps,
      guidanceScale: firstModel.cfg,
      costAlert: messages.cost ?? 0,
    });
  };

  const savePresetToFile = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "gembatch-preset.json";
    a.click();
  };

  const loadPresetFromFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        setConfig((c) => ({
          ...c,
          ...parsed,
          model: parsed.model
            ? Array.isArray(parsed.model)
              ? parsed.model
              : [parsed.model]
            : c.model,
        }));
      } catch {
        alert("Invalid preset file");
      }
    };
    reader.readAsText(file);
  };

  // Use prompt, model, size, cfg, steps, seed per image
  const generateBatch = async () => {
    if (!config.prompt.trim() || !options.sizes[config.imageSize]) return;
    setLoading(true);
    const newImages = [];
    const sz = options.sizes[config.imageSize];

    if (isMultiModel) {
      await Promise.all(
        config.model.map(async (modelKey) => {
          const modelConfig = options.models.find(m => m.apifragment === modelKey);
          try {
            const input = {
              prompt: config.prompt,
              image_size: { width: sz.width, height: sz.height },
              aspect_ratio: sz.aspect_ratio,
              num_inference_steps: modelConfig?.steps,
              guidance_scale: modelConfig?.cfg,
              enable_safety_checker: true,
            };
            if (/^\d+$/.test(config.seed)) {
              input.seed = +config.seed;
            }
            const res = await fal.subscribe(modelKey, {
              input,
              logs: true,
              onQueueUpdate: (upd) =>
                upd.status === "IN_PROGRESS" &&
                upd.logs.forEach((l) => console.log(l.message)),
            });
            const img = res.data.images?.[0];
            if (img?.url) {
              newImages.push({
                url: img.url,
                seed: res.data.seed || null,
                modelUsed: modelKey,
                cfg: modelConfig?.cfg,
                steps: modelConfig?.steps,
                imageSize: config.imageSize,
                prompt: config.prompt,
              });
            }
          } catch (e) {
            console.error("Error generating image:", e);
          }
        })
      );
    } else {
      await Promise.all(
        Array.from({ length: actualBatch }).map(async () => {
          const modelKey = config.model[0];
          const modelConfig = options.models.find(m => m.apifragment === modelKey);
          try {
            const input = {
              prompt: config.prompt,
              image_size: { width: sz.width, height: sz.height },
              aspect_ratio: sz.aspect_ratio,
              num_inference_steps: config.numSteps,
              guidance_scale: config.guidanceScale,
              enable_safety_checker: true,
            };
            if (/^\d+$/.test(config.seed)) {
              input.seed = +config.seed;
            }
            const res = await fal.subscribe(modelKey, {
              input,
              logs: true,
              onQueueUpdate: (upd) =>
                upd.status === "IN_PROGRESS" &&
                upd.logs.forEach((l) => console.log(l.message)),
            });
            const img = res.data.images?.[0];
            if (img?.url) {
              newImages.push({
                url: img.url,
                seed: res.data.seed || null,
                modelUsed: modelKey,
                cfg: config.guidanceScale,
                steps: config.numSteps,
                imageSize: config.imageSize,
                prompt: config.prompt,
              });
            }
          } catch (e) {
            console.error("Error generating image:", e);
          }
        })
      );
    }

    setImages((prev) => [...prev, ...newImages]);
    setLoading(false);
  };

  const initiateGenerate = () => {
    if (
      config.costAlert > 0 &&
      totalCost >= config.costAlert
    ) {
      setShowCostAlertModal(true);
    } else {
      generateBatch();
    }
  };
  const confirmGenerate = () => {
    setShowCostAlertModal(false);
    generateBatch();
  };

  const handleDeleteImage = (idx) => {
    setImages(images => images.filter((_, i) => i !== idx));
  };

  const handleUseImage = (img) => {
    setConfig(c => ({
      ...c,
      prompt: img.prompt || "",
      model: [img.modelUsed],
      imageSize: img.imageSize,
      numSteps: img.steps,
      guidanceScale: img.cfg,
      seed: img.seed ?? "",
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ----- BATCH INFO MESSAGES LOGIC -----
  let batchInfoMsg = "";
  if (isMultiModel) {
    batchInfoMsg = "Multiple models: Only one image per model.";
  } else if (config.seed) {
    batchInfoMsg = "Seed set: Batch locked to one image.";
  } else {
    batchInfoMsg = "Batch: Choose how many images to make.";
  }

  if (!optsLoaded || !initDone) {
    return <div>Loading options…</div>;
  }

  // --- Model dropdown as react-select
  const modelOptions = options.models.map(m => ({
    value: m.apifragment,
    label: `${m.comment ? m.comment : ""} ${formatCost(m.cost)}`,
  }));
  const selectedModelOptions = modelOptions.filter(opt =>
    config.model.includes(opt.value)
  );

  return (
    <div className={styles.container}>
      {showCostAlertModal && (
        <div className={styles.alertModal}>
          <div className={styles.alertContent}>
            <p style={{ fontSize: "1.25rem", color: "#111" }}>
              This Batch Cost is <span style={{ color: "#00b4d8" }}>{costDisplay}</span>
            </p>
            <div className={styles.alertButtons}>
              <button
                className={styles.alertButtonContinue}
                onClick={confirmGenerate}
              >
                Continue
              </button>
              <button
                className={styles.alertButtonCancel}
                onClick={() => setShowCostAlertModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LEFT COLUMN */}
      <div className={styles.leftColumn}>
        <div className={styles.subtitle}>What's Your Dream?</div>
        <textarea
          className={styles.promptInput}
          placeholder="Enter your image prompt…"
          value={config.prompt}
          onChange={(e) => handleChange("prompt", e.target.value)}
        />

        <div className={styles.dropdowns}>

          {/* REACT-SELECT MULTI DROPDOWN */}
          <Select
            isMulti
            styles={customSelectStyles}
            options={modelOptions}
            value={selectedModelOptions}
            onChange={handleModelChange}
            closeMenuOnSelect={false}
            placeholder="Select model(s)..."
            menuPlacement="auto"
            className="gems-model-select"
            
          />

          <select
            className={styles.select}
            value={config.imageSize}
            onChange={(e) =>
              handleChange("imageSize", e.target.value)
            }
          >
            {Object.keys(options.sizes).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>

          {/* Batch info message */}
          <div
            style={{
              fontSize: "0.8rem",
              color: "#00b4d8",
              margin: "3px 0 0 0",
              minHeight: "1.2em",
            }}
          >
            {batchInfoMsg}
          </div>

          <select
            className={styles.select}
            value={config.batch}
            disabled={/^\d+$/.test(config.seed) || isMultiModel}
            onChange={(e) => handleChange("batch", +e.target.value)}
          >
            {Array.from({ length: 20 }, (_, i) => i + 1).map(
              (n) => (
                <option key={n} value={n}>
                  {n} images
                </option>
              )
            )}
          </select>
        </div>

        <div className={styles.batchCost}>
          Batch Cost: {costDisplay}
        </div>

        <div className={styles.advancedSection}>
          <div
            className={styles.advancedHeader}
            onClick={() => setShowAdvanced((v) => !v)}
          >
            Advanced {showAdvanced ? "▲" : "▼"}
          </div>
          {showAdvanced && (
            <>
              <input
                type="number"
                min="0"
                placeholder="Seed (optional)"
                className={styles.narrowInput}
                value={config.seed}
                onChange={(e) =>
                  handleChange("seed", e.target.value)
                }
              />

              <div className={styles.sliderContainer}>
                <label
                  htmlFor="steps"
                  className={styles.sliderLabel}
                >
                  Inference Steps: {config.numSteps}
                </label>
                <input
                  id="steps"
                  type="range"
                  min="1"
                  max="40"
                  value={config.numSteps}
                  onChange={(e) =>
                    handleChange("numSteps", +e.target.value)
                  }
                  disabled={!advMode || isMultiModel}
                  className={
                    styles.slider +
                    (advMode && !isMultiModel ? "" : ` ${styles.disabledSlider}`)
                  }
                />
              </div>

              <div className={styles.sliderContainer}>
                <label
                  htmlFor="cfg"
                  className={styles.sliderLabel}
                >
                  CFG Scale:{" "}
                  {config.guidanceScale.toFixed(1)}
                </label>
                <input
                  id="cfg"
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={config.guidanceScale}
                  onChange={(e) =>
                    handleChange(
                      "guidanceScale",
                      parseFloat(e.target.value)
                    )
                  }
                  disabled={!advMode || isMultiModel}
                  className={
                    styles.slider +
                    (advMode && !isMultiModel ? "" : ` ${styles.disabledSlider}`)
                  }
                />
              </div>

              <div className={styles.costAlertSection}>
                <label htmlFor="costAlert">
                  Cost Alert (¢):
                </label>
                <input
                  type="number"
                  min="0"
                  id="costAlert"
                  className={styles.narrowInput}
                  value={config.costAlert}
                  onChange={e => handleChange("costAlert", +e.target.value)}
                  disabled={!advMode}
                  style={{
                    opacity: advMode ? 1 : 0.5,
                    pointerEvents: advMode ? 'auto' : 'none'
                  }}
                />
              </div>

              <div className={styles.presetButtons}>
                <button
                  className={styles.presetButton}
                  onClick={resetToDefaults}
                >
                  Reset
                </button>
                <button
                  className={styles.presetButton}
                  onClick={savePresetToFile}
                >
                  Export
                </button>
                <button
                  className={styles.presetButton}
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                >
                  Import
                </button>
              </div>
              <input
                type="file"
                accept="application/json"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={loadPresetFromFile}
              />
            </>
          )}
        </div>

        <button
          className={styles.generateButton}
          onClick={initiateGenerate}
          disabled={loading || !config.model || config.model.length === 0}
          style={{
            backgroundColor: (!config.model || config.model.length === 0) ? '#555' : '#00b4d8',
            cursor: (!config.model || config.model.length === 0 || loading) ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {(!config.model || config.model.length === 0) ? "Please Select 1 or More Models" :
            (loading ? "Generating…" : "Generate Image(s)")}
        </button>
      </div>

      {/* RIGHT COLUMN */}
      <div className={styles.rightColumn} style={{ position: "relative" }}>
        {/* GENERATING OVERLAY */}
        {loading && (
          <div className={styles.generatingOverlay}>
            <div className={styles.generatingAnim}>
              Making Dreams
            </div>
          </div>
        )}
        <div className={styles.thumbnailGrid}>
          {images.map((img, idx) => {
            const modelObj = options.models.find(
              (m) => m.apifragment === img.modelUsed
            );
            return (
              <div
                key={idx}
                className={styles.thumbnailWrapper}
              >
                <img
                  src={img.url}
                  alt={`Generated ${idx}`}
                  className={styles.thumbnail}
                  onClick={() => handleThumbnailClick(img.url)}
                />
                <div className={styles.imageInfoRow}>
                  <div className={styles.infoIconWrapper} tabIndex={0}>
                    <span className={styles.infoIconCircle}>i</span>
                    <div className={styles.infoTooltip}>
                      <div><strong>Seed:</strong> {img.seed !== undefined && img.seed !== null && img.seed !== "" ? img.seed : "N/A"}</div>
                      <div><strong>CFG:</strong> {img.cfg !== undefined ? img.cfg : "?"}</div>
                      <div><strong>Steps:</strong> {img.steps !== undefined ? img.steps : "?"}</div>
                      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
                        <a
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            handleUseImage(img);
                          }}
                          style={{
                            color: "#00b4d8",
                            textDecoration: "underline",
                            fontWeight: "normal",
                          }}
                        >
                          Use
                        </a>
                        <span style={{ color: "#00b4d8" }}>|</span>
                        <a
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            handleDeleteImage(idx);
                          }}
                          style={{
                            color: "#00b4d8",
                            textDecoration: "underline",
                            fontWeight: "normal",
                          }}
                        >
                          Delete
                        </a>
                      </div>
                    </div>
                  </div>
                  <span className={styles.modelText}>
                    {modelObj && modelObj.comment ? modelObj.comment : img.modelUsed}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modalUrl && (
        <div
          className={styles.modal}
          onClick={closeModal}
        >
          <img
            src={modalUrl}
            alt="Full View"
            className={styles.modalImage}
          />
        </div>
      )}
    </div>
  );
};

export default ImageGenerator;
